import { IOptions } from "constants/interfaces/options";
import { TFunction } from "i18next";

export enum NotificationTemplateType {
  PROMOTION = "PROMOTION",
  CAMPAIGN = "CAMPAIGN",
  NOTICE = "NOTICE",
  PAYMENT_REMINDER = "PAYMENT_REMINDER",
  PAYMENT_REMINDER_RENEWAL_SAME_PREMIUM = "PAYMENT_REMINDER_RENEWAL_SAME_PREMIUM",
  PAYMENT_REMINDER_RENEWAL_CHANGED_PREMIUM = "PAYMENT_REMINDER_RENEWAL_CHANGED_PREMIUM",
  OVERDUE_PAYMENT_REMINDER = "OVERDUE_PAYMENT_REMINDER",
  OVERDUE_PAYMENT_REMINDER_RENEWAL_SAME_PREMIUM = "OVERDUE_PAYMENT_REMINDER_RENEWAL_SAME_PREMIUM",
  OVERDUE_PAYMENT_REMINDER_RENEWAL_CHANGED_PREMIUM = "OVERDUE_PAYMENT_REMINDER_RENEWAL_CHANGED_PREMIUM",
  PROFILE_UPDATE_SUCCESS = "PROFILE_UPDATE_SUCCESS",
  PROFILE_UPDATE_FAILURE = "PROFILE_UPDATE_FAILURE",
  POLICY_APPROVED = "POLICY_APPROVED",
  POLICY_UNDER_REVIEW = "POLICY_UNDER_REVIEW",
  REQUEST_POLICY_CANCELLATION = "REQUEST_POLICY_CANCELLATION",
  POLICY_CANCELED = "POLICY_CANCELED",
  POLICY_NOT_APPROVED = "POLICY_NOT_APPROVED",
  REFUND_SUCCESSFUL = "REFUND_SUCCESSFUL",
  CANCELLATION_SUCCESSFUL = "CANCELLATION_SUCCESSFUL",
  AUTO_DEDUCTION = "AUTO_DEDUCTION",
}

export type NotificationChannel = "WEB_NOTIFICATION" | "EMAIL" | "SMS";

// Helper: สร้าง channelMap สำหรับ display
export const getNotificationChannelMap = (
  t: TFunction
): Record<NotificationChannel, string> => ({
  WEB_NOTIFICATION: t(".channelOptions.webNotification"),
  EMAIL: t(".channelOptions.email"),
  SMS: t(".channelOptions.sms"),
});

// Helper: สร้าง channel options array สำหรับ Select component
export const getNotificationChannelOptions = (t: TFunction): IOptions[] => {
  const channelMap = getNotificationChannelMap(t);
  return [
    { label: channelMap.WEB_NOTIFICATION, value: "WEB_NOTIFICATION" },
    { label: channelMap.EMAIL, value: "EMAIL" },
    { label: channelMap.SMS, value: "SMS" },
  ];
};

// Helper: แปลง enum เป็น array สำหรับใช้ใน Select
export const NOTIFICATION_TEMPLATE_TYPES = Object.values(
  NotificationTemplateType
);

export const CREATABLE_NOTIFICATION_TEMPLATE_TYPES = [
  NotificationTemplateType.PROMOTION,
  NotificationTemplateType.CAMPAIGN,
  NotificationTemplateType.NOTICE,
  NotificationTemplateType.PAYMENT_REMINDER,
  NotificationTemplateType.PAYMENT_REMINDER_RENEWAL_SAME_PREMIUM,
  NotificationTemplateType.PAYMENT_REMINDER_RENEWAL_CHANGED_PREMIUM,
  NotificationTemplateType.OVERDUE_PAYMENT_REMINDER,
  NotificationTemplateType.OVERDUE_PAYMENT_REMINDER_RENEWAL_SAME_PREMIUM,
  NotificationTemplateType.OVERDUE_PAYMENT_REMINDER_RENEWAL_CHANGED_PREMIUM,
  // NotificationTemplateType.PROFILE_UPDATE_SUCCESS,
  // NotificationTemplateType.PROFILE_UPDATE_FAILURE,
  // NotificationTemplateType.POLICY_APPROVED,
  // NotificationTemplateType.POLICY_UNDER_REVIEW,
  // NotificationTemplateType.REQUEST_POLICY_CANCELLATION,
  // NotificationTemplateType.POLICY_CANCELED,
  // NotificationTemplateType.POLICY_NOT_APPROVED,
  // NotificationTemplateType.REFUND_SUCCESSFUL,
  // NotificationTemplateType.CANCELLATION_SUCCESSFUL,
  // NotificationTemplateType.AUTO_DEDUCTION,
];

// Helper: สร้าง typeMap สำหรับ display
export const getNotificationTemplateTypeMap = (
  t: TFunction
): Record<string, string> => ({
  [NotificationTemplateType.PROMOTION]: t(".typeOptions.promotion"),
  [NotificationTemplateType.CAMPAIGN]: t(".typeOptions.campaign"),
  [NotificationTemplateType.NOTICE]: t(".typeOptions.notice"),
  [NotificationTemplateType.PAYMENT_REMINDER]: t(
    ".typeOptions.paymentReminder"
  ),
  [NotificationTemplateType.PAYMENT_REMINDER_RENEWAL_SAME_PREMIUM]: t(
    ".typeOptions.paymentReminderRenewalSamePremium"
  ),
  [NotificationTemplateType.PAYMENT_REMINDER_RENEWAL_CHANGED_PREMIUM]: t(
    ".typeOptions.paymentReminderRenewalChangedPremium"
  ),
  [NotificationTemplateType.OVERDUE_PAYMENT_REMINDER]: t(
    ".typeOptions.overduePaymentReminder"
  ),
  [NotificationTemplateType.OVERDUE_PAYMENT_REMINDER_RENEWAL_SAME_PREMIUM]: t(
    ".typeOptions.overduePaymentReminderRenewalSamePremium"
  ),
  [NotificationTemplateType.OVERDUE_PAYMENT_REMINDER_RENEWAL_CHANGED_PREMIUM]: t(
    ".typeOptions.overduePaymentReminderRenewalChangedPremium"
  ),
  [NotificationTemplateType.PROFILE_UPDATE_SUCCESS]: t(
    ".typeOptions.profileUpdateSuccess"
  ),
  [NotificationTemplateType.PROFILE_UPDATE_FAILURE]: t(
    ".typeOptions.profileUpdateFailure"
  ),
  [NotificationTemplateType.POLICY_APPROVED]: t(".typeOptions.policyApproved"),
  [NotificationTemplateType.POLICY_UNDER_REVIEW]: t(
    ".typeOptions.policyUnderReview"
  ),
  [NotificationTemplateType.REQUEST_POLICY_CANCELLATION]: t(
    ".typeOptions.requestPolicyCancellation"
  ),
  [NotificationTemplateType.POLICY_CANCELED]: t(".typeOptions.policyCanceled"),
  [NotificationTemplateType.POLICY_NOT_APPROVED]: t(
    ".typeOptions.policyNotApproved"
  ),
  [NotificationTemplateType.REFUND_SUCCESSFUL]: t(
    ".typeOptions.refundSuccessful"
  ),
  [NotificationTemplateType.CANCELLATION_SUCCESSFUL]: t(
    ".typeOptions.cancellationSuccessful"
  ),
  [NotificationTemplateType.AUTO_DEDUCTION]: t(".typeOptions.autoDeduction"),
});

// Helper: สร้าง options array สำหรับ Select component
export const getNotificationTemplateTypeOptions = (
  t: TFunction,
  types: NotificationTemplateType[] = NOTIFICATION_TEMPLATE_TYPES
): IOptions[] => {
  return types.map((type) => ({
    label: getNotificationTemplateTypeMap(t)[type],
    value: type,
  }));
};

// 9 type ที่ห้ามกรอก parameter
export const RESTRICTED_PARAMETER_TYPES = [
  NotificationTemplateType.PROFILE_UPDATE_SUCCESS,
  NotificationTemplateType.PROFILE_UPDATE_FAILURE,
  NotificationTemplateType.POLICY_APPROVED,
  NotificationTemplateType.POLICY_UNDER_REVIEW,
  NotificationTemplateType.REQUEST_POLICY_CANCELLATION,
  NotificationTemplateType.POLICY_CANCELED,
  NotificationTemplateType.POLICY_NOT_APPROVED,
  NotificationTemplateType.REFUND_SUCCESSFUL,
  NotificationTemplateType.CANCELLATION_SUCCESSFUL,
  NotificationTemplateType.AUTO_DEDUCTION,
];

// Helper: ตรวจสอบว่า type นี้ห้ามกรอก parameter หรือไม่
export const isParameterRestricted = (
  templateType: NotificationTemplateType | string
): boolean => {
  return RESTRICTED_PARAMETER_TYPES.includes(
    templateType as NotificationTemplateType
  );
};
